import React, { useMemo, useCallback, useEffect } from "react";
import moment from "moment-timezone";
import BigCalendar from './../../third-party/Calendar';
import CalendarStyle from './../../third-party/Calendar/css/bundle.css';
import ScheduleHeader from "./ScheduleHeader";
import { adjustColorBrightness, hexToRgba, isDarkColor } from "../../helpers/customfn";
import { HUMAN_TIMEZONES, ICAL_TIMEZONES } from "../../constants";

BigCalendar.momentLocalizer(moment);

const Calendar = ({
  events,
  onSelectEvent,
  onSelectSlot,
  onNavigate,
  onView,
  currentView,
  currentDate,
  loading,
  timezone,
  onCreate,
  bgColor,
  generateEmbedCode,
  theme // Added theme prop since it's used in calendarStyles
}) => {
  const resolvedTimezone = useMemo(() => {
    const tz = HUMAN_TIMEZONES[timezone] || timezone;
    return ICAL_TIMEZONES[tz] || tz || moment.tz.guess();
  }, [timezone]);

  const toWallClockDateInSelectedTimezone = useCallback((dateValue) => {
    if (!dateValue || !resolvedTimezone) return dateValue;

    const iso =
      dateValue instanceof Date
        ? dateValue.toISOString()
        : (moment.isMoment(dateValue) ? dateValue.toISOString() : dateValue);

    const m = moment.parseZone(iso);
    if (!m.isValid()) return dateValue;

    const z = m.clone().tz(resolvedTimezone);
    return new Date(
      z.year(),
      z.month(),
      z.date(),
      z.hour(),
      z.minute(),
      z.second(),
      z.millisecond()
    );
  }, [resolvedTimezone]);

  const startAccessor = useCallback(
    (event) => toWallClockDateInSelectedTimezone(event?.start),
    [toWallClockDateInSelectedTimezone]
  );

  const endAccessor = useCallback(
    (event) => {
      const duration = Number(event?.duration);
      const wallClockStart = toWallClockDateInSelectedTimezone(event?.start);

      // Keep end-time aligned to visible start + duration on DST-edge days.
      // This avoids end drift while preserving the existing start-time behavior.
      if (wallClockStart instanceof Date && Number.isFinite(duration) && duration > 0) {
        return moment(wallClockStart).add(duration, "minutes").toDate();
      }

      return toWallClockDateInSelectedTimezone(event?.end);
    },
    [toWallClockDateInSelectedTimezone]
  );

  const nowInSelectedTimezone = useMemo(
    () => toWallClockDateInSelectedTimezone(new Date()),
    [toWallClockDateInSelectedTimezone]
  );

  const displayDateInSelectedTimezone = useMemo(() => {
    const baseDate = currentDate || new Date();
    return toWallClockDateInSelectedTimezone(baseDate);
  }, [currentDate, toWallClockDateInSelectedTimezone]);


  const ENABLED_VIEWS = ['month', 'week', 'day', 'agenda'];

  const styles = {
    container: {
      width: '100%',
      backgroundColor: '#fbfbfb',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      flex: '1 1 auto',
    },
    paper: {
      flex: '1 1 0%',
      height: 0,
      overflow: 'hidden',
    },
  };


  const calendarStyles = useMemo(() => {

    const lightBgColor = bgColor ? hexToRgba(bgColor, 0.3) : 'rgba(0, 0, 0, 0.1)'; // 30% opacity
    const isDark = isDarkColor(bgColor);
    
    const contentColor = isDark ? bgColor : adjustColorBrightness(bgColor, -100);
  
    return {
      '.rbc-event': {
        'background-color': `${lightBgColor}`,
        'border-color': `${bgColor} !important`,
        color: `${contentColor} !important`,
      },
      '.rbc-event-content': {
        color: `${contentColor} !important`,
      },
      '.rbc-event.rbc-selected': {
        'background-color': `${bgColor} !important`,
        color: `${isDark ? adjustColorBrightness(bgColor, 150) : adjustColorBrightness(bgColor, -150)} !important`,
      },
      '.rbc-event:focus': {
        outline: `2px solid ${bgColor} !important`,
      }
    };
  }, [bgColor]);
  
  const getInlineStyles = useCallback(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      ${Object.entries(calendarStyles).map(([selector, styles]) => `
        ${selector} {
          ${Object.entries(styles).map(([prop, value]) => `${prop}: ${value};`).join(' ')}
        }
      `).join(' ')}
    `;
    return styleElement;
  }, [calendarStyles]);
  
  useEffect(() => {
    const styleElement = getInlineStyles();
    document.head.appendChild(styleElement);
    
    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, [getInlineStyles]);
  

  return (
      <BigCalendar
        tz={resolvedTimezone}
        loaded={!loading} // Assuming loaded means not loading
        paperStyle={styles.paper}
        selectable
        events={events}
        startAccessor={startAccessor}
        endAccessor={endAccessor}
        now={nowInSelectedTimezone}
        defaultView={currentView || "week"}
        view={currentView}
        date={displayDateInSelectedTimezone}
        onNavigate={onNavigate}
        onView={onView}
        scrollToTime={new Date(2016, 1, 1, 6)}
        defaultDate={displayDateInSelectedTimezone}
        views={ENABLED_VIEWS}
        onSelectEvent={onSelectEvent}
        onSelectSlot={onSelectSlot}
        onCreate={onCreate}
        
        generateEmbedCode={generateEmbedCode}
      />
  );
};

export default Calendar;